
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import NavBar from "@/components/NavBar";
import CourseSelector from "@/components/CourseSelector";
import GolferSelector from "@/components/GolferSelector";
import { useAuth } from "@/context/AuthContext";
import { useGameData } from "@/hooks/useGameData";
import { useFriendships } from "@/hooks/useFriendships";
import { adaptSupabaseUser } from "@/utils/userAdapter";
import LoadingView from "@/components/scoring/LoadingView";
import ScoringContainer from "@/components/scoring/ScoringContainer";
import { getEligibleGames, createActiveRound } from "@/utils/scoringUtils";
import { getCoPlayersFromActiveGames } from "@/integrations/supabase/game/gameQueryService";
import { getCourseById } from "@/integrations/supabase/courseService";
import { User, Course } from "@/types";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight } from "lucide-react";

const Scoring = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { friends, sentRequests, isLoading: isFriendsLoading, createManualFriend } = useFriendships();
  const [scoringStep, setScoringStep] = useState<'selectGolfers' | 'selectCourse' | 'scoring'>('selectGolfers');
  const [selectedGolferList, setSelectedGolferList] = useState<User[]>([]);
  const [allGolfers, setAllGolfers] = useState<User[]>([]);

  const realUser = user ? adaptSupabaseUser(user) : null;

  const {
    selectedGolfers,
    setSelectedGolfers,
    selectedCourse,
    setSelectedCourse,
    activeRound,
    setActiveRound,
    isLoading
  } = useGameData(gameId);

  // Merge friends + co-players from active games
  useEffect(() => {
    if (!user) return;
    const loadGolfers = async () => {
      const coPlayers = await getCoPlayersFromActiveGames(user.id);
      const currentUser = realUser;
      const merged = [...(currentUser ? [currentUser] : []), ...coPlayers, ...friends];
      const unique = Array.from(new Map(merged.map(u => [u.id, u])).values());
      setAllGolfers(unique);
    };
    loadGolfers();
  }, [user, friends]);

  // Auto-select current user when starting a new round
  useEffect(() => {
    if (realUser && selectedGolferList.length === 0 && !gameId) {
      setSelectedGolferList([realUser]);
    }
  }, [realUser]);

  // If we have loaded game data or restored an active round, move to scoring step
  if (selectedCourse && scoringStep !== 'scoring') {
    setScoringStep('scoring');
  }

  const handleGolferSelect = (golfer: User) => {
    if (!selectedGolferList.some(g => g.id === golfer.id)) {
      setSelectedGolferList(prev => [...prev, golfer]);
    }
  };

  const handleGolferRemove = (golferId: string) => {
    setSelectedGolferList(prev => prev.filter(g => g.id !== golferId));
  };

  const handleGolfersNext = () => {
    setSelectedGolfers(selectedGolferList);
    setScoringStep('selectCourse');
  };

  const handleCourseSelected = async (course: Course) => {
    // CourseSelector returns courses without holes — fetch the full course
    let fullCourse = course;
    if (!course.holes || course.holes.length === 0) {
      const fetched = await getCourseById(course.id);
      if (fetched) fullCourse = fetched;
    }

    setSelectedCourse(fullCourse);

    const eligibleGames = await getEligibleGames(fullCourse, selectedGolferList);
    const newRound = createActiveRound(selectedGolferList, fullCourse, eligibleGames);
    setActiveRound(newRound);

    setScoringStep('scoring');
  };

  if (isLoading || isFriendsLoading) {
    return (
      <div className="min-h-screen pb-16">
        <Header title="Loading Game..." />
        <LoadingView />
        <NavBar />
      </div>
    );
  }

  if (scoringStep === 'selectGolfers') {
    return (
      <div className="min-h-screen pb-16">
        <Header title="Select Golfers" />
        <div className="px-4">
          <GolferSelector
            selectedGolfers={selectedGolferList}
            onGolferSelect={handleGolferSelect}
            onGolferRemove={handleGolferRemove}
            allGolfers={allGolfers}
            confirmedFriends={friends}
            pendingFriends={sentRequests}
            showFriendStatus={true}
            onCreateManualPlayer={createManualFriend}
          />

          <div className="mt-6 flex justify-between items-center">
            <button
              onClick={() => navigate("/")}
              className="text-sm text-forest flex items-center gap-1"
            >
              <ArrowLeft className="h-3 w-3" />
              Back
            </button>
            <Button
              onClick={handleGolfersNext}
              disabled={selectedGolferList.length === 0}
              className="bg-forest hover:bg-forest/90 text-white flex items-center"
            >
              Next
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
          {selectedGolferList.length === 0 && (
            <p className="text-sm text-muted-foreground mt-2 text-center">Select at least one golfer to continue</p>
          )}
        </div>
        <NavBar />
      </div>
    );
  }

  if (scoringStep === 'selectCourse') {
    return (
      <div className="min-h-screen pb-16">
        <Header title="Select Course" />
        <div className="px-4 mb-2">
          <button
            onClick={() => setScoringStep('selectGolfers')}
            className="text-sm text-forest flex items-center gap-1"
          >
            <ArrowLeft className="h-3 w-3" />
            Back
          </button>
        </div>
        <CourseSelector onSelectCourse={handleCourseSelected} />
        <NavBar />
      </div>
    );
  }

  if (scoringStep === 'scoring' && selectedCourse) {
    return (
      <ScoringContainer
        golfers={selectedGolfers}
        course={selectedCourse}
        eligibleGames={activeRound?.eligibleGames || []}
        gameId={gameId}
      />
    );
  }

  // Default fallback
  return (
    <div className="min-h-screen pb-16">
      <Header title="Scoring" />
      <LoadingView message="Initializing..." />
      <NavBar />
    </div>
  );
};

export default Scoring;
